import { SuperPop, T2, TEXT_LENGTH, getTranslatedText } from '@nile/components';
import { Button } from '@superops/ui-design-system';
import { includes } from 'lodash';
import React, { useState, useContext, useEffect } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import styled from 'styled-components';
import { SVGIcon } from '@superops/super-icons';
import { ASSETS_LIST } from '@nile/rmm/src/constants.ts';
import PolicyContext from '@nile/banyan/src/Context/PolicyContext';

const Input = styled.input`
  height: 30px;
  border: none;
  border-bottom: 1px solid #ececec;
  background-color: transparent;
  margin-bottom: 10px;
  outline: none;
  width: 100%;
`;

function MultiTextFieldWithLabel(props) {
  const {
    placeholder,
    label,
    name,
    deleteButton,
    onItemAdded,
    onItemDeleted,
    className,
    labelClassName,
    charCodes,
    values: inputValues,
    shouldAddOnBlur,
    addHelper,
    handleValidation,
    readOnly,
    intl,
    info,
    appendColon
  } = props;
  const [values, setValues] = useState([]);
  const [value, setValue] = useState('');
  const policyContext = useContext(PolicyContext);

  const addColonAtEnd = (text) => {
    if (policyContext?.value?.classId === ASSETS_LIST.WINDOWS && appendColon) {
      if (!text.endsWith(':')) {
        return `${text}:`;
      }
    }
    return text;
  };
  useEffect(() => {
    setValues(inputValues || []);
  }, [inputValues?.length]);
  const handleItemAdd = (val) => {
    if (
      includes(values, val) ||
      !val ||
      (handleValidation && !handleValidation(val))
    ) {
      setValue(val);
      return;
    }
    const newValues = Array.from(
      new Set([...values, ...[addColonAtEnd(value)]])
    );
    setValues(newValues);
    setValue('');
    onItemAdded(addColonAtEnd(value), newValues);
  };

  const handleItemRemove = (value) => {
    const newValues = values.filter((v) => v !== value);
    onItemDeleted(value, newValues);
    setValues(newValues);
  };

  const handleBlur = (e) => {
    // 13: Enter
    if (shouldAddOnBlur) {
      e.preventDefault();
      handleItemAdd(e.target.value);
    }
  };
  /** Removing special characters regex validation, backend will support all special characters now */
  const handleValueChange = (e) => {
    setValue(e.target.value);
  };

  const handleKeypress = (e) => {
    if (includes(charCodes, e.charCode)) {
      e.preventDefault();
      handleItemAdd(e.target.value);
    }
  };

  return (
    <div className="multiple-value-text-input flex actr">
      <div className="mr8">
        <FormattedMessage id="admin.policyManagement.alertPolicy.policies.exclude" />
      </div>
      <div className="w300 mr8">
        <ItemsHolder className="items-holder">
          {values.length > 0
            ? values.map((el) => {
                return (
                  <Item>
                    <div className="item-font">{el}</div>
                    <Delete
                      className="multi-text-delete"
                      tabIndex="-1"
                      role="button"
                      onKeyPress={() => handleItemRemove(el)}
                      onClick={() => handleItemRemove(el)}
                    >
                      {deleteButton}
                    </Delete>
                  </Item>
                );
              })
            : ''}
        </ItemsHolder>
        <Input
          name={name}
          placeholder={placeholder || 'Type value and press enter'}
          value={value}
          type="text"
          onKeyPress={handleKeypress}
          onChange={handleValueChange}
          onBlur={handleBlur}
          className={className}
          maxLength={TEXT_LENGTH.size_4}
          readOnly={readOnly}
        />
      </div>
      {addHelper && (
        <Button
          onClick={() => {
            handleItemAdd(value);
          }}
          plain
        >
          Add
        </Button>
      )}
      <div className="mb10">
        <SuperPop
          position="top-start"
          content={getTranslatedText(intl, info)}
          holder={<SVGIcon name="About" />}
        />
      </div>
    </div>
  );
}

const ItemsHolder = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-top: 10px;
  max-width: 300px;
`;

const Item = styled.div`
  display: flex;
  align-items: center;
  background: #ececec;
  background-color: rgba(196, 196, 196, 0.2);
  border-radius: 2px;
  padding: 4px 8px;
  margin: 2px;
  align-items: center;
  height: 24px;
  font-weight: 500;
  font-size: 14px;
  line-height: 21;
  color: rgba(0, 0, 0, 0.8);
  .item-font {
    border-radius: 2px;
    color: hsl(0, 0%, 20%);
    font-size: 85%;
    overflow: hidden;
    padding: 3px;
    padding-left: 6px;
    text-overflow: ellipsis;
    white-space: nowrap;
    box-sizing: border-box;
  }
`;

const Delete = styled.div`
  margin-left: 8px;
  cursor: pointer;
  outline: none;
`;

MultiTextFieldWithLabel.defaultProps = {
  placeholder: '',
  charCodes: [13],
  deleteButton: <span>&times;</span>,
  values: [],
  label: '',
  shouldAddOnBlur: true,
  className: '',
  labelClassName: ''
};

export default injectIntl(MultiTextFieldWithLabel);
